import { fhirR4 } from "@smile-cdr/fhirts";

// ============================================================
// AWS HealthLake Headers
// ============================================================

export const AMZ_CONSISTENCY_HEADER_STRONG = { "x-amz-fhir-history-consistency-level": "strong" } as const;
export const IF_MATCH_HEADER = "If-Match" as const;

// ============================================================
// FHIR Coding System URLs
// ============================================================

export const LOINC_SYSTEM = "http://loinc.org";
export const UCUM_SYSTEM = "http://unitsofmeasure.org";
export const VITALL_DOC_SYSTEM = "https://vitall.com/documentation";
export const VITALL_MESSAGE_ORDER_SYSTEM = "https://vitall.com/documentation/messageOrder";
export const VITALL_MESSAGE_SOURCE_SYSTEM = "https://vitall.com/documentation/messageSource";
export const VITALL_MESSAGE_TYPE_SYSTEM = "https://vitall.com/documentation/messageType";
export const SNOMED_SYSTEM = "http://snomed.info/sct";
export const HL7_PRACTITIONER_ROLE_SYSTEM = "http://terminology.hl7.org/CodeSystem/practitioner-role";
export const HL7_OBS_CATEGORY_SYSTEM = "http://terminology.hl7.org/CodeSystem/observation-category";
export const HL7_OBS_INTERPRETATION_SYSTEM = "http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation";
export const VITALL_OBS_INTERPRETATION_SYSTEM = "https://vitall.com/fhir/CodeSystem/ObservationInterpretation";
export const VITALL_YES_NO_SYSTEM = "https://vitall.com/fhir/CodeSystem/yesNo";
export const VITALL_CHF_STATE_SYSTEM = "https://vitall.com/fhir/CodeSystem/algorithmResult/chf/state";
export const VITALL_SEVERITY_SYSTEM = "https://vitall.com/fhir/CodeSystem/algorithmResult/severity";
export const VITALL_CHF_WITH_MED_SYSTEM = "https://vitall.com/fhir/CodeSystem/algorithmResult/chf/with-med";

export enum YesNoCode {
  Yes = "YES",
  No = "NO",
}

export enum ChfStateCode {
  STATE_1 = "1",
  STATE_2 = "2",
  STATE_3 = "3",
  STATE_4 = "4",
  STATE_5 = "5",
  STATE_6 = "6",
  STATE_7 = "7",
  STATE_8 = "8",
  STATE_9 = "9",
}

export enum WithMedCode {
  WITH_MED = "WITH_MED",
  WITHOUT_MED = "WITHOUT_MED",
}

// ============================================================
// CHF Symptom LinkIds (from Questionnaire/symptomsCongestiveHeartFailure)
// ============================================================

export enum ChfSymptomLinkId {
  Fainted = "fainted",
  IcdFired = "icd_fired",
  LvadAlarm = "lvad_alarm",
  LvadStool = "lvad_stool",
  LvadDriveline = "lvad_driveline",
  WorseNightBreathing = "worse_night_breathing",
  MoreChestPain = "more_chest_pain",
  MoreTired = "more_tired",
  MoreShortOfBreath = "more_short_of_breath",
  SwollenAnkles = "swollen_ankles",
  LightHeaded = "lightheaded",
  UnusualHeartBeat = "unusual_heart_beat",
  StopDailyActivities = "stop_daily_activities",
  NumberOfPillows = "number_of_pillows",
}

export const VITAL_SIGNS_CATEGORY = [
  { coding: [{ system: HL7_OBS_CATEGORY_SYSTEM, code: "vital-signs", display: "Vital Signs" }] },
];
export const MEDLY_YES_NO_SYSTEM = "http://medly.ca/fhir/CodeSystem/yesNo";
export const VITALL_TASK_TYPE_SYSTEM = "https://vitall.com/fhir/task-type";
export const ADHERENCE_ALERT_CODE = "adherence";
export const ABNORMAL_READING_CODE = "abnormal-reading";

// ============================================================
// Condition Coding (CHF)
// ============================================================

export const CONDITION_CODING = {
  snomed: { system: "http://snomed.info/sct", code: "42343007", display: "Congestive heart failure" } as fhirR4.Coding,
};

// ============================================================
// Practitioner Role Coding (PractitionerRole.code)
// ============================================================

export const PRACTITIONER_ROLE_CODING = {
  nurse: { system: HL7_PRACTITIONER_ROLE_SYSTEM, code: "nurse", display: "Nurse" } as fhirR4.Coding,
  nursePractitioner: { system: SNOMED_SYSTEM, code: "224571005", display: "Nurse practitioner" } as fhirR4.Coding,
  doctor: { system: HL7_PRACTITIONER_ROLE_SYSTEM, code: "doctor", display: "Physician" } as fhirR4.Coding,
};

/** Normalise legacy role display values returned by the FHIR server. */
const ROLE_DISPLAY_OVERRIDES: Record<string, string> = {
  Doctor: "Physician",
};

export const normalizeRoleDisplay = (display: string): string => ROLE_DISPLAY_OVERRIDES[display] ?? display;

// ============================================================
// Threshold System
// ============================================================

export const HEART_FAILURE_THRESHOLD_SYSTEM = "https://vitall.com/fhir/CodeSystem/heartFailureThreshold";

export enum ThresholdCode {
  NORMAL = "N",
  LOW = "L",
  HIGH = "H",
  ABNORMAL = "A",
  INDETERMINATE = "IND",
  VERY_LOW = "LU",
  VERY_HIGH = "HU",
  CRITICALLY_ABNORMAL = "AA",
  CRITICALLY_LOW = "LL",
  CRITICALLY_HIGH = "HH",
}

export enum VitalInterpretationCode {
  NORMAL = "N",
  LOW = "L",
  HIGH = "H",
  ABNORMAL = "A",
  INDETERMINATE = "IND",
  VERY_LOW = "LU",
  VERY_HIGH = "HU",
  CRITICALLY_ABNORMAL = "AA",
  CRITICALLY_LOW = "LL",
  CRITICALLY_HIGH = "HH",
  MODERATELY_ABNORMAL = "AM",
  SEVERELY_ABNORMAL = "AU",
  HIGH_NO_DELTA = "H1",
  EXPECTED = "EXP",
  UNEXPECTED = "UNE",
}

export const THRESHOLD_DISPLAY: Record<ThresholdCode, string> = {
  [ThresholdCode.NORMAL]: "Normal",
  [ThresholdCode.LOW]: "Low",
  [ThresholdCode.HIGH]: "High",
  [ThresholdCode.ABNORMAL]: "Abnormal",
  [ThresholdCode.INDETERMINATE]: "Indeterminate",
  [ThresholdCode.VERY_LOW]: "Very Low",
  [ThresholdCode.VERY_HIGH]: "Very High",
  [ThresholdCode.CRITICALLY_ABNORMAL]: "Critically Abnormal",
  [ThresholdCode.CRITICALLY_LOW]: "Critically Low",
  [ThresholdCode.CRITICALLY_HIGH]: "Critically High",
};

// ============================================================
// Vital Sign Codes (primary identifiers for goal lookup)
// ============================================================

export const VITAL_CODES = {
  bpPanel: {
    system: LOINC_SYSTEM,
    code: "85354-9",
    display: "Blood pressure panel with all children optional",
  } as fhirR4.Coding,
  bodyWeight: { system: LOINC_SYSTEM, code: "29463-7", display: "Body weight" } as fhirR4.Coding,
  bodyWeightDelta: { system: "http://snomed.info/sct", code: "248346009", display: "Weight change" } as fhirR4.Coding,
  systolic: { system: LOINC_SYSTEM, code: "8480-6", display: "Systolic blood pressure" } as fhirR4.Coding,
  diastolic: { system: LOINC_SYSTEM, code: "8462-4", display: "Diastolic blood pressure" } as fhirR4.Coding,
  pulse: { system: LOINC_SYSTEM, code: "8867-4", display: "Heart rate" } as fhirR4.Coding,
};

// Additional coding per vital sign (LOINC, SNOMED)
export const BODY_WEIGHT_EXTRA_CODING: fhirR4.Coding[] = [
  { system: LOINC_SYSTEM, code: "29463-7", display: "Body weight" },
  { system: LOINC_SYSTEM, code: "3141-9", display: "Body weight Measured" },
  { system: "http://snomed.info/sct", code: "27113001", display: "Body weight (observable entity)" },
];

export const WEIGHT_DELTA_EXTRA_CODING: fhirR4.Coding[] = [
  { system: "http://snomed.info/sct", code: "248346009", display: "Weight change" },
];

export const SYSTOLIC_EXTRA_CODING: fhirR4.Coding[] = [
  { system: LOINC_SYSTEM, code: "8480-6", display: "Systolic blood pressure" },
  { system: "http://snomed.info/sct", code: "271649006", display: "Systolic blood pressure" },
];

export const DIASTOLIC_EXTRA_CODING: fhirR4.Coding[] = [
  { system: LOINC_SYSTEM, code: "8462-4", display: "Diastolic blood pressure" },
  { system: "http://snomed.info/sct", code: "271650006", display: "Diastolic blood pressure" },
];

export const PULSE_EXTRA_CODING: fhirR4.Coding[] = [
  { system: LOINC_SYSTEM, code: "8867-4", display: "Heart rate" },
  { system: "http://snomed.info/sct", code: "364075005", display: "Heart rate (observable entity)" },
];

// ============================================================
// Units (UCUM)
// ============================================================

export const UNIT_KG = { unit: "kg", system: UCUM_SYSTEM, code: "kg" };
export const UNIT_G = { unit: "g", system: UCUM_SYSTEM, code: "g" };
export const UNIT_LBS = { unit: "lbs", system: UCUM_SYSTEM, code: "[lb_av]" };
export const UNIT_MMHG = { unit: "mmHg", system: UCUM_SYSTEM, code: "mm[Hg]" };
export const UNIT_BPM = { unit: "beats/minute", system: UCUM_SYSTEM, code: "/min" };

// ============================================================
// Patient Condition Extension URLs
// ============================================================

const PATIENT_EXT_BASE = "https://vitall.com/documentation/extensions/patients";
export const PATIENT_EXT_ICD = `${PATIENT_EXT_BASE}/isPatientICD`;
export const PATIENT_EXT_LVAD = `${PATIENT_EXT_BASE}/isPatientLVAD`;
export const PATIENT_EXT_MEDICATION = `${PATIENT_EXT_BASE}/isPatientTakesMedicationOnWeightGain`;
export const PATIENT_EXT_MEDICATION_INFO = `${PATIENT_EXT_BASE}/medicationOnWeightGainInstructions`;
export const PATIENT_CONDITION_URLS = [
  PATIENT_EXT_ICD,
  PATIENT_EXT_LVAD,
  PATIENT_EXT_MEDICATION,
  PATIENT_EXT_MEDICATION_INFO,
];

// ============================================================
// ContactPoint (Telecom) Constants
// ============================================================

// ============================================================
// Device Reading Context Extension
// ============================================================

export const DEVICE_READING_CONTEXT_URL = "https://vitall.com/documentation/extensions/device-reading-context";
export const DEVICE_READING_CONTEXT_SYSTEM = "https://vitall.com/documentation/extensions/Context-Codes";

export enum ReadingContextCode {
  Morning = "MRNING",
  Extra = "EXTRA",
  Overnight = "OVRNGHT",
  Historical = "HISTRCL",
  Bedtime = "BEDTIME",
  Fasting = "FASTNG",
  PreBreakfast = "PRE_BRKFST",
  PostBreakfast = "POST_BRKFST",
  PreLunch = "PRE_LUNCH",
  PostLunch = "POST_LUNCH",
  PreDinner = "PRE_DINNER",
  PostDinner = "POST_DINNER",
  PreSnack = "PRE_SNACK",
  PostSnack = "POST_SNACK",
}

// ============================================================
// CommunicationRequest Coding Systems
// ============================================================

export const COMMUNICATION_CATEGORY_SYSTEM = "http://terminology.hl7.org/CodeSystem/communication-category";
export const COMMUNICATION_CATEGORY_REMINDER = "reminder";
export const COMMUNICATION_CATEGORY_ALERT = "alert";
export const PARTICIPATION_MODE_SYSTEM = "https://vitall.com/fhir/CodeSystem/communicationMedium";
export const PARTICIPATION_MODE_PHONE = "PHONE";
export const PARTICIPATION_MODE_SMS = "SMS";

export const TELECOM_SYSTEM_EMAIL = "email";
export const TELECOM_SYSTEM_PHONE = "phone";
export const TELECOM_USE_HOME = "home";
export const TELECOM_USE_MOBILE = "mobile";

// ============================================================
// CareTeam Constants
// ============================================================

export const CARETEAM_STATUS_ACTIVE = "active" as const;

// Default chunk size for participant reference queries to avoid 403 errors with too many comma-separated values
export const CARETEAM_PARTICIPANT_CHUNK_SIZE = 50;
export const PRACTITIONER_ROLE_CHUNK_SIZE = 75;
